import type { PassportSchemaDef } from "../types.js";

/**
 * EARLY-POSITIONING DRAFT — the ESPR working plan 2025-2030 covers electronics
 * & ICT; several product groups already carry Ecodesign/Energy-labelling and
 * RoHS/WEEE obligations that the DPP will consolidate. Modeled on the EU
 * Energy Label classes, the (EU) 2023/1670 smartphone/tablet ecodesign
 * repairability + spare-parts + software-update requirements, RoHS substance
 * restrictions, and WEEE collection categories.
 *
 * Reference template for the electrical/energy family (see energy-related-v1).
 * Field ids are STABLE; trued up when the delegated act lands by adding fields
 * / flipping `required`/`tier`, never by renaming ids.
 */
export const electronicsV1: PassportSchemaDef = {
  id: "electronics-v1",
  version: "1.0.0-draft",
  title: {
    tr: "Elektronik ve ICT Ürün Pasaportu",
    en: "Electronics & ICT Product Passport",
    de: "Elektronik- und IKT-Produktpass",
  },
  jsonLdContext: {
    "@version": 1.1,
    "@vocab": "https://vocab.idpp.net/electronics/v1#",
    schema: "https://schema.org/",
    gs1: "https://gs1.org/voc/",
    identifier: "gs1:gtin",
    issuer: "schema:issuedBy",
    issuedAt: "schema:dateCreated",
  },
  categories: [
    {
      id: "identity",
      labels: { tr: "Ürün Kimliği", en: "Product Identity", de: "Produktidentität" },
      fields: [
        {
          id: "identity.productName",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Ürün adı", en: "Product name", de: "Produktname" },
        },
        {
          id: "identity.productDescription",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Ürün özeti", en: "Product summary", de: "Produktbeschreibung" },
          description: "Short description of the product and its intended use.",
          presentation: { kind: "summary" },
        },
        {
          id: "identity.gtin",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "GTIN", en: "GTIN", de: "GTIN" },
          description: "GS1 Global Trade Item Number (8/12/13/14 digits).",
        },
        {
          id: "identity.deviceCategory",
          type: "enum",
          required: true,
          tier: "public",
          enumValues: [
            "smartphone",
            "tablet",
            "laptop",
            "desktop",
            "monitor",
            "server",
            "networking",
            "printer",
            "wearable",
            "other-ict",
            "other",
          ],
          labels: { tr: "Cihaz kategorisi", en: "Device category", de: "Gerätekategorie" },
        },
        {
          id: "identity.brand",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Marka", en: "Brand", de: "Marke" },
        },
        {
          id: "identity.model",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Model", en: "Model", de: "Modell" },
        },
        {
          id: "identity.manufacturerName",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Üretici adı", en: "Manufacturer name", de: "Herstellername" },
        },
        {
          id: "identity.countryOfManufacture",
          type: "country",
          required: true,
          tier: "public",
          labels: { tr: "Üretim ülkesi", en: "Country of manufacture", de: "Herstellungsland" },
          description: "ISO 3166-1 alpha-2 code (e.g. TR).",
        },
        {
          id: "identity.releaseDate",
          type: "date",
          required: false,
          tier: "public",
          labels: { tr: "Piyasaya sürüm tarihi", en: "Release date", de: "Markteinführungsdatum" },
        },
        {
          id: "identity.serialOrBatch",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "Seri/parti numarası", en: "Serial/batch number", de: "Serien-/Chargennummer" },
        },
        {
          id: "identity.commodityCode",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "Emtia kodu (GTİP/CN)", en: "Commodity code (CN/HS)", de: "Warennummer (KN/HS)" },
          description: "Needed for the EU Registry registration record.",
        },
      ],
    },
    {
      id: "energy",
      labels: { tr: "Enerji", en: "Energy", de: "Energie" },
      fields: [
        {
          id: "energy.energyEfficiencyClass",
          type: "enum",
          required: true,
          tier: "public",
          enumValues: ["a", "b", "c", "d", "e", "f", "g", "not-rated"],
          labels: {
            tr: "Enerji verimlilik sınıfı",
            en: "Energy efficiency class",
            de: "Energieeffizienzklasse",
          },
          highlight: true,
        },
        {
          id: "energy.powerConsumptionOperatingW",
          type: "number",
          required: false,
          tier: "public",
          unit: "W",
          labels: {
            tr: "Çalışma güç tüketimi",
            en: "Operating power consumption",
            de: "Leistungsaufnahme im Betrieb",
          },
        },
        {
          id: "energy.standbyPowerW",
          type: "number",
          required: false,
          tier: "public",
          unit: "W",
          labels: { tr: "Bekleme gücü", en: "Standby power", de: "Standby-Leistung" },
        },
        {
          id: "energy.annualEnergyKwh",
          type: "number",
          required: false,
          tier: "public",
          unit: "kWh/year",
          labels: {
            tr: "Yıllık enerji tüketimi",
            en: "Annual energy consumption",
            de: "Jährlicher Energieverbrauch",
          },
          highlight: true,
        },
        {
          id: "energy.powerSupplyType",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Güç kaynağı türü", en: "Power supply type", de: "Netzteiltyp" },
        },
        {
          id: "energy.ecodesignConformity",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Ekotasarım uygunluğu", en: "Ecodesign conformity", de: "Ökodesign-Konformität" },
          description: "Ecodesign regulation the product conforms to.",
        },
        {
          id: "energy.energyLabelUrl",
          type: "url",
          required: false,
          tier: "public",
          labels: { tr: "Enerji etiketi bağlantısı", en: "Energy label link", de: "Energielabel-Link" },
        },
      ],
    },
    {
      id: "materials",
      labels: { tr: "Malzemeler ve Maddeler", en: "Materials & Substances", de: "Materialien & Stoffe" },
      fields: [
        {
          id: "materials.rohsCompliant",
          type: "boolean",
          required: true,
          tier: "public",
          labels: { tr: "RoHS uyumlu", en: "RoHS compliant", de: "RoHS-konform" },
          description: "Restriction of Hazardous Substances (Directive 2011/65/EU).",
        },
        {
          id: "materials.rohsExemptions",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "RoHS muafiyetleri", en: "RoHS exemptions", de: "RoHS-Ausnahmen" },
        },
        {
          id: "materials.svhcPresent",
          type: "boolean",
          required: false,
          tier: "public",
          labels: {
            tr: "SVHC içerir (>%0,1 a/a)",
            en: "Contains SVHC (>0.1% w/w)",
            de: "Enthält SVHC (>0,1 % w/w)",
          },
        },
        {
          id: "materials.svhcSubstances",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "SVHC madde listesi", en: "SVHC substances", de: "SVHC-Stoffe" },
        },
        {
          id: "materials.criticalRawMaterialsPresent",
          type: "boolean",
          required: false,
          tier: "public",
          labels: {
            tr: "Kritik ham madde içerir",
            en: "Contains critical raw materials",
            de: "Enthält kritische Rohstoffe",
          },
          description: "E.g. rare earths, cobalt, tantalum (EU CRM list).",
        },
        {
          id: "materials.criticalRawMaterialsDetail",
          type: "string",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Kritik ham madde detayı",
            en: "Critical raw materials detail",
            de: "Details zu kritischen Rohstoffen",
          },
        },
        {
          id: "materials.recycledContentPct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: { tr: "Geri dönüştürülmüş içerik", en: "Recycled content", de: "Rezyklatanteil" },
        },
        {
          id: "materials.plasticContentPct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: { tr: "Plastik içeriği", en: "Plastic content", de: "Kunststoffanteil" },
        },
      ],
    },
    {
      id: "repairability",
      labels: {
        tr: "Onarılabilirlik ve Yazılım",
        en: "Repairability & Software",
        de: "Reparierbarkeit & Software",
      },
      fields: [
        {
          id: "repairability.repairabilityScore",
          type: "enum",
          required: false,
          tier: "public",
          enumValues: ["A", "B", "C", "D", "E"],
          labels: { tr: "Onarılabilirlik sınıfı", en: "Repairability score", de: "Reparierbarkeitsklasse" },
          highlight: true,
        },
        {
          id: "repairability.sparePartsAvailable",
          type: "boolean",
          required: true,
          tier: "public",
          labels: { tr: "Yedek parça mevcut", en: "Spare parts available", de: "Ersatzteile verfügbar" },
        },
        {
          id: "repairability.sparePartsYears",
          type: "number",
          required: false,
          tier: "public",
          unit: "years",
          labels: {
            tr: "Yedek parça bulunabilirlik süresi",
            en: "Spare parts availability period",
            de: "Ersatzteil-Verfügbarkeitsdauer",
          },
          highlight: true,
        },
        {
          id: "repairability.repairInstructionsUrl",
          type: "url",
          required: false,
          tier: "public",
          labels: { tr: "Onarım talimatları", en: "Repair instructions", de: "Reparaturanleitung" },
        },
        {
          id: "repairability.disassemblyInfo",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "Söküm bilgisi", en: "Disassembly information", de: "Demontageinformationen" },
        },
        {
          id: "repairability.batteryRemovability",
          type: "enum",
          required: false,
          tier: "public",
          enumValues: ["user-removable", "tool-removable", "non-removable", "no-battery"],
          labels: { tr: "Batarya çıkarılabilirliği", en: "Battery removability", de: "Akku-Entnehmbarkeit" },
        },
        {
          id: "repairability.softwareSupportYears",
          type: "number",
          required: false,
          tier: "public",
          unit: "years",
          labels: {
            tr: "Yazılım destek süresi",
            en: "Software support period",
            de: "Software-Unterstützungsdauer",
          },
          description: "Guaranteed years of security/functional updates.",
          highlight: true,
        },
        {
          id: "repairability.softwareUpdatePolicyUrl",
          type: "url",
          required: false,
          tier: "public",
          labels: {
            tr: "Yazılım güncelleme politikası",
            en: "Software update policy",
            de: "Software-Update-Richtlinie",
          },
        },
        {
          id: "repairability.warrantyYears",
          type: "number",
          required: false,
          tier: "public",
          unit: "years",
          labels: { tr: "Garanti süresi", en: "Warranty period", de: "Garantiedauer" },
        },
      ],
    },
    {
      id: "circularity",
      labels: { tr: "Döngüsellik", en: "Circularity", de: "Kreislauffähigkeit" },
      fields: [
        {
          id: "circularity.recyclable",
          type: "boolean",
          required: true,
          tier: "public",
          labels: { tr: "Geri dönüştürülebilir", en: "Recyclable", de: "Recycelbar" },
          highlight: true,
        },
        {
          id: "circularity.recyclabilityPct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: {
            tr: "Geri dönüştürülebilirlik oranı",
            en: "Recyclability rate",
            de: "Recyclingfähigkeitsquote",
          },
        },
        {
          id: "circularity.weeeCategory",
          type: "enum",
          required: false,
          tier: "public",
          enumValues: [
            "1-temperature-exchange",
            "2-screens",
            "3-lamps",
            "4-large-equipment",
            "5-small-equipment",
            "6-small-it",
            "other",
          ],
          labels: { tr: "WEEE kategorisi", en: "WEEE category", de: "WEEE-Kategorie" },
        },
        {
          id: "circularity.collectionInfo",
          type: "string",
          required: true,
          tier: "public",
          labels: {
            tr: "Ömür sonu toplama bilgisi",
            en: "End-of-life collection information",
            de: "Informationen zur Altgerätesammlung",
          },
          description: "How to return the product for WEEE collection/recycling.",
        },
        {
          id: "circularity.takeBackProgramUrl",
          type: "url",
          required: false,
          tier: "public",
          labels: { tr: "Geri alma programı", en: "Take-back program", de: "Rücknahmeprogramm" },
        },
        {
          id: "circularity.hazardousComponentsRemoval",
          type: "string",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Tehlikeli bileşen çıkarma bilgisi",
            en: "Hazardous component removal",
            de: "Entfernung gefährlicher Komponenten",
          },
          description: "For recyclers: batteries, capacitors, mercury lamps needing separate handling.",
        },
      ],
    },
  ],
};
